import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { FormClassRetire, FormKbn } from "../../assets/data/Constants";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import TkStepper from "../../components/atoms/TkStepper";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkCheckbox from "../../components/atoms/TkCheckbox";
import TkFileUpload from "../../components/molecules/TkFileUpload";
import { BusinessContext } from "../../context/businessContext";
import { GlobalContext } from "../../context/globalContext";
import { fakeTableList, stepper } from "./parts";
import { IAPI_RET002_Response, IAPI_RET004_Response } from "../../type/apiType";
import { httpAPI_RET002, httpAPI_RET004 } from "./api/request";
import { useConcatRoutes } from "../../routes/router";
import _ from "lodash";
import { labelids } from "../../assets/data/TkExplanationProperty";
import "./_style.scss";

/**
 * SCRR410004 退職手当金の請求手続き（退職者確認）
 * 退職手当金請求者が退職者の情報を確認する。
 * @return SCRR410004
 */
const SCRR410004 = () => {
  const navigate = useNavigate();

  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.job_36;
  const [res, setRes] = useState<IAPI_RET004_Response & IAPI_RET002_Response>(
    {}
  );
  const [stateTableList, setStateTableList] = useState<any>([]);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));
  const [checkOne, setCheckOne] = useState(false);
  const [checkTwo, setCheckTwo] = useState(false);
  const [allChecked, setAllChecked] = useState(false);
  const [file, setFile] = useState<any>([]);
  let routeList = useConcatRoutes();
  const location = useLocation();
  const path = location.pathname;
  const route = routeList.find((route) => route.path === path);
  const businessType = businessState.common?.businessType;

  useEffect(() => {
    if (route && route.meta) {
      if (businessType === "JOB-35") {
        route.meta.title = "退職手当金の請求手続き（退職者確認）";
      }
      if (businessType === "JOB-36") {
        route.meta.title = "合算制度利用申出の手続き（退職者確認）";
      }
    }
  }, [businessType, route]);

  const handleCallApiInit = async () => {
    try {
      let res: IAPI_RET004_Response & IAPI_RET002_Response = {};
      if (businessType === "JOB-35") {
        // 退職手当金の請求手続き（退職者確認）表示API実行
        res = await httpAPI_RET004({
          contractorNo:
            state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
              ? businessState.common?.contractorNo
              : state.authInfo?.contractorNo,
          staffNo: businessState.job_35?.staffNo,
          applyId: businessState.job_35?.applyId,
        });

        setBusinessState({
          ...businessState,
          job_36: {
            ...context,
            tempSaveStep: res.tempSaveStep,
          },
        });
      } else {
        // 合算制度利用申出（1回目）参照API実行
        res = await httpAPI_RET002({
          contractorNo:
            state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO
              ? businessState.common?.contractorNo
              : state.authInfo?.contractorNo,
          staffNo: context?.staffNo,
          applyId: context?.applyId,
        });
      }

      if (res.resultCd === "I0000") {
        let resArr = stateFakeTableList.current;

        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;

          targetObj && (targetObj.text = res[i as keyof typeof res]);
        }

        resArr.find((item) => item.keyword === "period")!.text = `${
          res.maePeriodYear ?? ""
        }年${res.maePeriodMonth ?? ""}ヶ月`;
        resArr.find((item) => item.keyword === "facility")!.text = `${
          res.facilityNo ?? res.finalFacilityNo ?? ""
        }:${res.facilityName ?? ""}`;

        if (
          businessType === "JOB-36" &&
          state.authInfo?.userType ===
            authorityUserType.userTypeNumber.KEIYAKUSYA
        ) {
          let file = resArr.find((item) => item.keyword === "file")!;
          let targetFile = (res as IAPI_RET002_Response).fileList?.find(
            (item) => item.formClass === FormClassRetire["委任状"]
          );
          file.node = (
            <Box className="node file">
              <Typography>{labelids.labelid.LBLR410004_04}</Typography>
              <TkFileUpload
                wantToSetValue={
                  targetFile
                    ? [
                        {
                          formName: targetFile.fileName,
                          fileBase64: targetFile.fileBase64,
                        },
                      ]
                    : []
                }
                onChange={(e: any) => setFile(e.target?.value)}
              />
            </Box>
          );
          file.showLine = true;
        }

        setStateTableList(resArr);

        setRes(res);
        setCheckOne(!!res.applyId);
        setCheckTwo(businessType === "JOB-35" && !!res.applyId);
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    handleCallApiInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let resArr = stateFakeTableList.current;
    let check = resArr.find((item) => item.keyword === "check")!;
    let isDisable = businessType === "JOB-36" && res.resultCd !== "I0000";

    check.node = (
      <Box className="node checkbox">
        <Box>
          <Typography>
            ＜個人情報の取り扱いについて＞
            <br />
            請求者又は退職者に係る個人情報は、退職手当共済業務及び付帯する業務の範囲内で利用されます。
          </Typography>
          <TkCheckbox
            label="個人情報の取り扱いについて同意します"
            checked={checkOne}
            onChange={(e: any) => {
              setCheckOne(e.target.checked);
            }}
            disabled={isDisable}
          />
        </Box>

        {businessType !== "JOB-36" && (
          <Box>
            <Typography>
              ＜留意事項について＞
              <br />
              ・退職手当金を請求いただいた場合であっても、審査により支給できない場合があります。
              <br />
              ・退職手当金の支給財源の都合により、支給時期が遅れることがあります。
            </Typography>
            <TkCheckbox
              label="上記の内容について了解します"
              checked={checkTwo}
              onChange={(e: any) => {
                setCheckTwo(e.target.checked);
              }}
            />
          </Box>
        )}
      </Box>
    );

    setStateTableList(resArr);

    if (businessType === "JOB-35") {
      setAllChecked(checkOne && checkTwo);
    } else {
      if (
        state.authInfo?.userType === authorityUserType.userTypeNumber.KEIYAKUSYA
      ) {
        setAllChecked(checkOne && file?.length);
      } else {
        setAllChecked(checkOne);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkOne, checkTwo, file, res]);

  const handleSubmit = () => {
    // 退職時処理区分が「退職手当金」の場合
    if (businessType === "JOB-35") {
      navigate("/SCRR411105");
    }

    // 退職時処理区分が「合算」の場合
    if (businessType === "JOB-36") {
      setBusinessState({
        ...businessState,
        common: {
          ...businessState.common,
          contractorNo: res.contractorNo ?? businessState.common?.contractorNo,
        },
        job_36: {
          ...context,
          applyId: res.applyId ?? context?.applyId,
          tempSaveStep: res.tempSaveStep ?? context?.tempSaveStep,
          contractorName: res.contractorName,
          staffNo: res.staffNo ?? context?.staffNo,
          facilityNo: res.facilityNo ?? context?.facilityNo,
          staffName: res.staffNameKanji ?? context?.staffName,
          birthday: res.birthday ?? context?.birthday,
          retireDate: res.retireDate ?? context?.retireDate,
          retireProcedureReason:
            res.retireProcedureReason ?? context?.retireProcedureReason,
          zipcode: res.zipcode ?? context?.zipcode,
          prefectureCd: res.prefectureCd ?? context?.prefectureCd,
          addressCity: res.addressCity ?? context?.addressCity,
          addressOthers: res.addressOthers ?? context?.addressOthers,
          tel: res.tel ?? context?.tel,
          email: res.email ?? context?.email,
          maePeriodYear: res.maePeriodYear ?? context?.maePeriodYear,
          maePeriodMonth: res.maePeriodMonth ?? context?.maePeriodMonth,
          facilityName: res.facilityName ?? context?.facilityName,
          agentEmail: res.agentEmail ?? context?.agentEmail,
          updateDatetime: res.updateDatetime,
          fileList:
            file.length > 0
              ? [
                  {
                    serialNo: 1,
                    fileList_staffNo: res.staffNo,
                    formKbn: FormKbn["退職書類"],
                    formClass: FormClassRetire["委任状"],
                    formName: file[0]?.formName,
                    file: file[0]?.fileBase64,
                  },
                ]
              : [],
        },
      });
      navigate("/SCRR411114");
    }
  };

  return (
    <Box className="SCRR410004" data-testid="SCRR410004test">
      <TkStepper
        steps={stepper}
        activeStep={0}
        savaStep={context?.tempSaveStep}
      />

      <TkFakeTable
        list={stateTableList}
        ruleSchema={{}}
        submitButtonText="次へ"
        buttonGroup={<TkBackButton />}
        onCheckAndSubmit={handleSubmit}
        disabled={!allChecked}
      />
    </Box>
  );
};

export default SCRR410004;
